import React, { useState, useEffect } from 'react';
import { XMarkIcon, SparklesIcon, PencilIcon, LockClosedIcon, MagnifyingGlassIcon, LinkIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { CreateDeckDto } from '../types';
import { t } from '../utils/translations';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { deckService } from '../services/deckService';
import FileUpload from './FileUpload';
import ErrorDisplay from './ErrorDisplay';

interface DeckCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateManual: (deckData: CreateDeckDto) => Promise<void>;
  onCreateWithAI: (aiRequest: AIDecksRequest) => Promise<{ success: boolean; message: string } | void>;
  editingDeck?: any;
  deckForm: CreateDeckDto;
  setDeckForm: (form: CreateDeckDto) => void;
  subscription?: any;
  onUpgradePrompt?: () => void;
}

interface AIDecksRequest {
  topic: string;
  description: string;
  cardCount: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  language: 'portuguese' | 'english' | 'spanish';
  additionalInfo?: string;
  userEmail?: string;
}

const DeckCreationModal: React.FC<DeckCreationModalProps> = ({
  isOpen,
  onClose,
  onCreateManual,
  onCreateWithAI,
  editingDeck,
  deckForm,
  setDeckForm,
  subscription,
  onUpgradePrompt
}) => {
  const { isAdmin } = useAuth();
  const isAndroidWebView = isUpmindyAndroidWebView();
  const [step, setStep] = useState<'choice' | 'manual' | 'ai' | 'file'>('choice');
  const [aiSubStep, setAiSubStep] = useState(1); // Track AI form sub-steps (1-4)
  const [fileSubStep, setFileSubStep] = useState(1); // Track File form sub-steps (1-3)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingSlug, setIsGeneratingSlug] = useState(false);
  const [currentSlug, setCurrentSlug] = useState<string>('');
  
  // AI form state
  const [aiForm, setAIForm] = useState<AIDecksRequest>({
    topic: '',
    description: '',
    cardCount: 20,
    difficulty: 'intermediate',
    language: 'portuguese',
    additionalInfo: ''
  });

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileUploadForm, setFileUploadForm] = useState({
    cardCount: 20,
    difficulty: 'intermediate' as 'beginner' | 'intermediate' | 'advanced',
    language: 'portuguese' as 'portuguese' | 'english' | 'spanish',
    additionalInfo: ''
  });
  const [fileError, setFileError] = useState<any>(null);

  // Update currentSlug when editingDeck changes
  useEffect(() => {
    if (editingDeck) {
      setCurrentSlug(editingDeck.slug || '');
    }
  }, [editingDeck]);

  // Update cardCount when subscription changes
  useEffect(() => {
    if (subscription?.subscriptionTier === 'free' && aiForm.cardCount > 20) {
      setAIForm(prev => ({ ...prev, cardCount: 20 }));
    }
  }, [subscription?.subscriptionTier, aiForm.cardCount]);

  // Set the correct initial step based on whether we're editing or creating
  useEffect(() => {
    if (isOpen) {
      setStep(editingDeck ? 'manual' : 'choice');
    }
  }, [isOpen, editingDeck]);

  const resetModal = () => {
    setStep(editingDeck ? 'manual' : 'choice');
    setAiSubStep(1);
    setFileSubStep(1);
    setCurrentSlug(editingDeck?.slug || '');
    setAIForm({
      topic: '',
      description: '',
      cardCount: 20,
      difficulty: 'intermediate',
      language: 'portuguese',
      additionalInfo: ''
    });
    setSelectedFiles([]);
    setFileUploadForm({
      cardCount: subscription?.subscriptionTier === 'free' ? 10 : 20,
      difficulty: 'intermediate',
      language: 'portuguese',
      additionalInfo: ''
    });
    setFileError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!deckForm.name.trim()) {
      toast.error(t('Deck name is required'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreateManual(deckForm);
      handleClose();
    } catch (error) {
      console.error('Error creating deck:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAISubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!aiForm.topic.trim() || !aiForm.description.trim()) {
      toast.error('Tópico e descrição são obrigatórios');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await onCreateWithAI(aiForm);
      toast.success('Deck criado com sucesso! Já está disponível na sua biblioteca.');
      handleClose();
    } catch (error: any) {
      console.error('Error submitting AI request:', error);
      
      // Handle deck limit error with upgrade prompt
      if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        } else {
          toast.error('Você atingiu o limite de 3 decks no plano gratuito. Faça upgrade para criar mais decks!');
        }
      }
      // Handle AI card limit error with upgrade prompt
      else if (error.response?.data?.code === 'AI_CARD_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        } else {
          toast.error('Usuários gratuitos podem criar decks de IA com máximo 20 cards. Faça upgrade para Premium!');
        }
      } else {
        toast.error('Erro ao criar deck com IA. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      toast.error('Por favor, selecione um arquivo');
      return;
    }

    setIsSubmitting(true);
    setFileError(null);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));
      formData.append('cardCount', fileUploadForm.cardCount.toString());
      formData.append('difficulty', fileUploadForm.difficulty);
      formData.append('language', fileUploadForm.language);
      if (fileUploadForm.additionalInfo) {
        formData.append('additionalInfo', fileUploadForm.additionalInfo);
      }

      const response = await deckService.createDeckFromFile(formData);
      toast.success(response.message || 'Deck criado com sucesso a partir do arquivo!');
      handleClose();
      // Optionally redirect to the deck
      if (response.deckId) {
        setTimeout(() => {
          window.location.href = `/decks/management/${response.deckId}`;
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error creating deck from file:', error);
      
      // Store the full error object for ErrorDisplay component
      setFileError(error);
      
      // Handle subscription limits
      if (error.response?.data?.code === 'AI_CARD_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateSlug = async () => {
    if (!editingDeck?._id) {
      toast.error('Só é possível gerar slug para decks existentes');
      return;
    }

    setIsGeneratingSlug(true);
    try {
      const response = await deckService.generateSlug(editingDeck._id);
      setCurrentSlug(response.slug);
      toast.success(`Slug gerado: ${response.slug}`);
    } catch (error: any) {
      console.error('Error generating slug:', error);
      if (error.response?.status === 403) {
        toast.error('Apenas administradores podem gerar slugs');
      } else {
        toast.error('Erro ao gerar slug. Tente novamente.');
      }
    } finally {
      setIsGeneratingSlug(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-4 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal content */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-3 pt-4 pb-3 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-full max-w-[95vw] max-h-[95vh] overflow-y-auto">
          
          {/* Header */}
          <div className="flex items-start justify-between mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 pr-2 leading-tight">
              {step === 'choice' ? 'Como deseja criar seus flashcards?' : 
               step === 'manual' ? (editingDeck ? 'Editar Deck' : 'Criar Flashcards Manualmente') :
               step === 'file' ? 'Criar Deck a partir de Arquivo' :
               'Criar Deck com IA'}
            </h3>
            <button
              onClick={handleClose}
              className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Step: Choice */}
          {step === 'choice' && (
            <div className="space-y-3 sm:space-y-4">
              
              {/* AI Option */}
              <button
                onClick={() => setStep('ai')}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">
                      Criar com IA
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Nossa IA criará cards personalizados baseados no seu tópico de interesse
                    </p>
                  </div>
                </div>
              </button>

              {/* File Upload Option */}
              <button
                onClick={() => setStep('file')}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <DocumentArrowUpIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-purple-700 dark:group-hover:text-purple-300">
                      Upload de Arquivo
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Envie até 5 arquivos (PDF, DOCX ou TXT) e a IA criará cards automaticamente
                    </p>
                  </div>
                </div>
              </button>

              {/* Search Option */}
              <button
                onClick={() => {
                  handleClose();
                  window.location.href = '/search';
                }}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <MagnifyingGlassIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                      Explorar Flashcards
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Copie flashcards compartilhados pela comunidade
                    </p>
                  </div>
                </div>
              </button>

              {/* Manual Option */}
              <button
                onClick={() => setStep('manual')}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-500 dark:hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <PencilIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                      Criar Manualmente
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Crie um flashcard vazio e adicione seus próprios cards conforme necessário
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step: Manual Form */}
          {step === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  {t('decks.deckName')}
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={deckForm.name}
                  onChange={(e) => setDeckForm({ ...deckForm, name: e.target.value })}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-3 sm:px-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  placeholder={t('decks.enterDeckName')}
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  {t('decks.description')} ({t('common.optional')})
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={deckForm.description}
                  onChange={(e) => setDeckForm({ ...deckForm, description: e.target.value })}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-3 sm:px-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-base"
                  placeholder={t('decks.describeDeck')}
                />
              </div>

              {/* Show current slug for editing decks */}
              {editingDeck && (
                <div>
                  <label htmlFor="slug" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Slug Atual
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      id="slug"
                      value={currentSlug || 'Nenhum slug definido'}
                      readOnly
                      className={`block flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-600 text-sm font-mono ${
                        currentSlug 
                          ? 'text-gray-700 dark:text-gray-300' 
                          : 'text-gray-500 dark:text-gray-400 italic'
                      }`}
                    />
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleGenerateSlug}
                        disabled={isGeneratingSlug}
                        className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <LinkIcon className="h-4 w-4 mr-1" />
                        {isGeneratingSlug ? 'Gerando...' : 'Gerar Slug'}
                      </button>
                    )}
                  </div>
                  {isAdmin && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Gera um slug único baseado no nome do deck para URLs amigáveis.
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                {!editingDeck && (
                  <button
                    type="button"
                    onClick={() => setStep('choice')}
                    className="w-full sm:w-auto px-4 py-3 sm:px-6 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-base font-medium"
                  >
                    Voltar
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-4 py-3 sm:px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 transition-colors text-base font-medium"
                >
                  {isSubmitting ? 'Criando...' : (editingDeck ? 'Atualizar Deck' : 'Criar Flashcard')}
                </button>
              </div>
            </form>
          )}

          {/* Step: AI Form */}
          {step === 'ai' && (
            <form onSubmit={handleAISubmit} className="space-y-5 sm:space-y-6">
              {/* Progress Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  {[1, 2, 3, 4].map((stepNum) => (
                    <React.Fragment key={stepNum}>
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                            aiSubStep > stepNum
                              ? 'bg-green-500 text-white'
                              : aiSubStep === stepNum
                              ? 'bg-indigo-600 text-white ring-4 ring-indigo-200 dark:ring-indigo-800'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {aiSubStep > stepNum ? '✓' : stepNum}
                        </div>
                        <span className={`text-xs mt-1 text-center hidden sm:block ${
                          aiSubStep >= stepNum ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-400'
                        }`}>
                          {stepNum === 1 ? 'Tópico' : stepNum === 2 ? 'Detalhes' : stepNum === 3 ? 'Configuração' : 'Revisão'}
                        </span>
                      </div>
                      {stepNum < 4 && (
                        <div className={`h-1 flex-1 mx-1 rounded transition-all duration-300 ${
                          aiSubStep > stepNum ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="sm:hidden text-center">
                  <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                    {aiSubStep === 1 ? 'Tópico' : aiSubStep === 2 ? 'Detalhes' : aiSubStep === 3 ? 'Configuração' : 'Revisão'}
                  </span>
                </div>
              </div>

              {/* Step 1: Topic */}
              {aiSubStep === 1 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <SparklesIcon className="h-12 w-12 text-indigo-500 mx-auto mb-3" />
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Sobre o que você quer aprender?
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Digite o tema principal do seu deck de estudos
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="topic" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Tópico do Deck *
                    </label>
                    <input
                      type="text"
                      id="topic"
                      required
                      value={aiForm.topic}
                      onChange={(e) => setAIForm({ ...aiForm, topic: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!aiForm.topic.trim()) {
                            toast.error('Por favor, insira um tópico');
                            return;
                          }
                          setAiSubStep(aiSubStep + 1);
                        }
                      }}
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base transition-all"
                      placeholder="Ex: JavaScript para Iniciantes, História do Brasil..."
                      autoFocus
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setAIForm({ ...aiForm, topic: 'JavaScript para Iniciantes' })}
                        className="px-3 py-1.5 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                      >
                        JavaScript
                      </button>
                      <button
                        type="button"
                        onClick={() => setAIForm({ ...aiForm, topic: 'História do Brasil' })}
                        className="px-3 py-1.5 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                      >
                        História
                      </button>
                      <button
                        type="button"
                        onClick={() => setAIForm({ ...aiForm, topic: 'Biologia Celular' })}
                        className="px-3 py-1.5 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                      >
                        Biologia
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Description */}
              {aiSubStep === 2 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <PencilIcon className="h-12 w-12 text-indigo-500 mx-auto mb-3" />
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Descreva o que deseja aprender
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Quanto mais detalhes, melhor será o resultado
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="description" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Descrição Detalhada *
                    </label>
                    <textarea
                      id="description"
                      rows={5}
                      required
                      value={aiForm.description}
                      onChange={(e) => setAIForm({ ...aiForm, description: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          if (aiForm.description.length < 20) {
                            toast.error('A descrição deve ter pelo menos 20 caracteres');
                            return;
                          }
                          setAiSubStep(aiSubStep + 1);
                        }
                      }}
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-base transition-all"
                      placeholder="Descreva detalhadamente o que você quer aprender, quais conceitos devem ser cobertos, nível de profundidade, etc."
                      autoFocus
                    />
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                      <span>Mínimo 20 caracteres</span>
                      <span className={aiForm.description.length >= 20 ? 'text-green-600 dark:text-green-400' : ''}>
                        {aiForm.description.length} caracteres
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Configuration */}
              {aiSubStep === 3 && (
                <div className="space-y-5 animate-fadeIn">
                  <div className="text-center mb-6">
                    <svg className="h-12 w-12 text-indigo-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Configure seu deck
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Personalize quantidade, dificuldade e idioma
                    </p>
                  </div>

                  <div>
                    <label htmlFor="cardCount" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Quantidade de Cards
                    </label>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {[10, 20, 30, 50, 100, 500].map((count) => {
                        const isLocked = count > 20 && subscription?.subscriptionTier === 'free';
                        const isSelected = aiForm.cardCount === count;
                        return (
                          <button
                            key={count}
                            type="button"
                            disabled={isLocked}
                            onClick={() => {
                              if (!isLocked) {
                                setAIForm({ ...aiForm, cardCount: count });
                              } else if (onUpgradePrompt) {
                                onUpgradePrompt();
                              }
                            }}
                            className={`relative px-3 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                              isSelected
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-md scale-105'
                                : isLocked
                                ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {count}
                            {isLocked && (
                              <LockClosedIcon className="h-3 w-3 absolute top-1 right-1 text-amber-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {subscription?.subscriptionTier === 'free' && !isAndroidWebView && (
                      <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg flex items-start">
                        <LockClosedIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                        <span>
                          Mais de 20 cards disponível apenas para Premium.{' '}
                          <a 
                            href="https://upmindy.com/pricing" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline font-semibold hover:text-amber-700 dark:hover:text-amber-300"
                          >
                            Ver planos
                          </a>
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="difficulty" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Nível de Dificuldade
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'beginner', label: 'Iniciante', icon: '🌱' },
                          { value: 'intermediate', label: 'Intermediário', icon: '🌿' },
                          { value: 'advanced', label: 'Avançado', icon: '🌳' }
                        ].map((diff) => (
                          <button
                            key={diff.value}
                            type="button"
                            onClick={() => setAIForm({ ...aiForm, difficulty: diff.value as any })}
                            className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                              aiForm.difficulty === diff.value
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className="text-xl mr-3">{diff.icon}</span>
                            {diff.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="language" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Idioma dos Cards
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'portuguese', label: 'Português', flag: '🇧🇷' },
                          { value: 'english', label: 'English', flag: '🇺🇸' },
                          { value: 'spanish', label: 'Español', flag: '🇪🇸' }
                        ].map((lang) => (
                          <button
                            key={lang.value}
                            type="button"
                            onClick={() => setAIForm({ ...aiForm, language: lang.value as any })}
                            className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                              aiForm.language === lang.value
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className="text-xl mr-3">{lang.flag}</span>
                            {lang.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Informações Adicionais (Opcional)
                    </label>
                    <textarea
                      id="additionalInfo"
                      rows={3}
                      value={aiForm.additionalInfo}
                      onChange={(e) => setAIForm({ ...aiForm, additionalInfo: e.target.value })}
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm transition-all"
                      placeholder="Alguma preferência específica, estilo de pergunta, exemplos que gostaria de ver, etc."
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Review */}
              {aiSubStep === 4 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <div className="h-12 w-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Tudo pronto!
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Revise as informações antes de criar seu deck
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 rounded-xl p-5 space-y-4 border-2 border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Tópico</h5>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{aiForm.topic}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSubStep(1)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 p-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex items-start justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex-1">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Descrição</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{aiForm.description}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSubStep(2)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 p-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Cards</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{aiForm.cardCount} cards</p>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Dificuldade</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {aiForm.difficulty === 'beginner' ? '🌱 Iniciante' : aiForm.difficulty === 'intermediate' ? '🌿 Intermediário' : '🌳 Avançado'}
                        </p>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Idioma</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {aiForm.language === 'portuguese' ? '🇧🇷 Português' : aiForm.language === 'spanish' ? '🇪🇸 Español' : '🇺🇸 English'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSubStep(3)}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-medium text-left flex items-center"
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Editar configuração
                      </button>
                    </div>

                    {aiForm.additionalInfo && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Info Adicional</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{aiForm.additionalInfo}</p>
                      </div>
                    )}
                  </div>

                  {/* Information message about email notification */}
                  {!isSubmitting && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1 text-sm">
                          <p className="text-blue-700 dark:text-blue-200 font-semibold mb-1">
                            💡 Você receberá notificação por email
                          </p>
                          <p className="text-blue-600 dark:text-blue-300">
                            Pode continuar navegando! Notificaremos você quando o deck estiver pronto (geralmente 30-60 segundos).
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Progress Bar - Show when submitting */}
              {isSubmitting && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-5 border-2 border-indigo-200 dark:border-indigo-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <SparklesIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 animate-pulse" />
                      <span className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">
                        Criando seu deck com IA...
                      </span>
                    </div>
                    <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                      ⏱️ ~30-60s
                    </span>
                  </div>
                  
                  {/* Animated Progress Bar */}
                  <div className="w-full bg-indigo-100 dark:bg-indigo-800 rounded-full h-2.5 overflow-hidden">
                    <div className="h-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 dark:from-indigo-400 dark:to-purple-400 rounded-full animate-pulse transition-all duration-1000 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-3 text-center">
                    Nossa IA está analisando seu tópico e criando cards personalizados...
                  </p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Back Button */}
                <button
                  type="button"
                  onClick={() => {
                    if (aiSubStep > 1) {
                      setAiSubStep(aiSubStep - 1);
                    } else {
                      setStep('choice');
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex-1 px-5 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← {aiSubStep === 1 ? 'Voltar' : 'Anterior'}
                </button>

                {/* Next/Submit Button */}
                {aiSubStep < 4 ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Validation for each step
                      if (aiSubStep === 1 && !aiForm.topic.trim()) {
                        toast.error('Por favor, insira um tópico');
                        return;
                      }
                      if (aiSubStep === 2 && aiForm.description.length < 20) {
                        toast.error('A descrição deve ter pelo menos 20 caracteres');
                        return;
                      }
                      setAiSubStep(aiSubStep + 1);
                    }}
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-indigo-500/30"
                  >
                    Continuar →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Criando...</span>
                      </div>
                    ) : (
                      <span className="flex items-center justify-center">
                        <SparklesIcon className="h-5 w-5 mr-2" />
                        Criar Deck com IA
                      </span>
                    )}
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Step: File Upload Form */}
          {step === 'file' && (
            <form onSubmit={handleFileSubmit} className="space-y-5 sm:space-y-6">
              {/* Progress Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  {[1, 2, 3].map((stepNum) => (
                    <React.Fragment key={stepNum}>
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                            fileSubStep > stepNum
                              ? 'bg-green-500 text-white'
                              : fileSubStep === stepNum
                              ? 'bg-purple-600 text-white ring-4 ring-purple-200 dark:ring-purple-800'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {fileSubStep > stepNum ? '✓' : stepNum}
                        </div>
                        <span className={`text-xs mt-1 text-center hidden sm:block ${
                          fileSubStep >= stepNum ? 'text-purple-600 dark:text-purple-400 font-medium' : 'text-gray-400'
                        }`}>
                          {stepNum === 1 ? 'Arquivo' : stepNum === 2 ? 'Configuração' : 'Revisão'}
                        </span>
                      </div>
                      {stepNum < 3 && (
                        <div className={`h-1 flex-1 mx-1 rounded transition-all duration-300 ${
                          fileSubStep > stepNum ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="sm:hidden text-center">
                  <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                    {fileSubStep === 1 ? 'Arquivo' : fileSubStep === 2 ? 'Configuração' : 'Revisão'}
                  </span>
                </div>
              </div>

              {/* Error Display */}
              {fileError && (
                <ErrorDisplay
                  error={fileError}
                  onRetry={() => setFileError(null)}
                  onUpgrade={onUpgradePrompt}
                  compact={false}
                />
              )}

              {/* Step 1: File Upload */}
              {fileSubStep === 1 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <DocumentArrowUpIcon className="h-12 w-12 text-purple-500 mx-auto mb-3" />
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Selecione seu arquivo
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Envie até 5 arquivos PDF, TXT, DOC ou DOCX para gerar cards automaticamente
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Selecione até 5 Arquivos *
                    </label>
                    <FileUpload
                      onFilesChange={(files) => {
                        setSelectedFiles(files);
                        setFileError(null);
                      }}
                      selectedFiles={selectedFiles}
                      error={null}
                      disabled={isSubmitting}
                      acceptedTypes={['.pdf', '.txt', '.doc', '.docx']}
                      maxSizeMB={10}
                    />
                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      📄 <strong>Tipos aceitos:</strong> PDF, TXT, DOC, DOCX<br />
                      📊 <strong>Tamanho máximo:</strong> 10MB por arquivo (até 5 arquivos)
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Configuration */}
              {fileSubStep === 2 && (
                <div className="space-y-5 animate-fadeIn">
                  <div className="text-center mb-6">
                    <svg className="h-12 w-12 text-purple-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Configure seu deck
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Personalize quantidade, dificuldade e idioma
                    </p>
                  </div>

                  <div>
                    <label htmlFor="fileCardCount" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Quantidade de Cards
                    </label>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {[5, 10, 20, 30, 50, 100].map((count) => {
                        const isLocked = count > 20 && subscription?.subscriptionTier === 'free';
                        const isSelected = fileUploadForm.cardCount === count;
                        return (
                          <button
                            key={count}
                            type="button"
                            disabled={isLocked}
                            onClick={() => {
                              if (!isLocked) {
                                setFileUploadForm({ ...fileUploadForm, cardCount: count });
                              } else if (onUpgradePrompt) {
                                onUpgradePrompt();
                              }
                            }}
                            className={`relative px-3 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                              isSelected
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 shadow-md scale-105'
                                : isLocked
                                ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {count}
                            {isLocked && (
                              <LockClosedIcon className="h-3 w-3 absolute top-1 right-1 text-amber-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {subscription?.subscriptionTier === 'free' && !isAndroidWebView && (
                      <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg flex items-start">
                        <LockClosedIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                        <span>
                          Mais de 20 cards disponível apenas para Premium.{' '}
                          <a 
                            href="https://upmindy.com/pricing" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline font-semibold hover:text-amber-700 dark:hover:text-amber-300"
                          >
                            Ver planos
                          </a>
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fileDifficulty" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Nível de Dificuldade
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'beginner', label: 'Iniciante', icon: '🌱' },
                          { value: 'intermediate', label: 'Intermediário', icon: '🌿' },
                          { value: 'advanced', label: 'Avançado', icon: '🌳' }
                        ].map((diff) => (
                          <button
                            key={diff.value}
                            type="button"
                            onClick={() => setFileUploadForm({ ...fileUploadForm, difficulty: diff.value as any })}
                            className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                              fileUploadForm.difficulty === diff.value
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className="text-xl mr-3">{diff.icon}</span>
                            {diff.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="fileLanguage" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Idioma dos Cards
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'portuguese', label: 'Português', flag: '🇧🇷' },
                          { value: 'english', label: 'English', flag: '🇺🇸' },
                          { value: 'spanish', label: 'Español', flag: '🇪🇸' }
                        ].map((lang) => (
                          <button
                            key={lang.value}
                            type="button"
                            onClick={() => setFileUploadForm({ ...fileUploadForm, language: lang.value as any })}
                            className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                              fileUploadForm.language === lang.value
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className="text-xl mr-3">{lang.flag}</span>
                            {lang.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fileAdditionalInfo" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Informações Adicionais (Opcional)
                    </label>
                    <textarea
                      id="fileAdditionalInfo"
                      rows={3}
                      value={fileUploadForm.additionalInfo}
                      onChange={(e) => setFileUploadForm({ ...fileUploadForm, additionalInfo: e.target.value })}
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm transition-all"
                      placeholder="Contexto adicional, foco específico, etc."
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Review */}
              {fileSubStep === 3 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Tudo pronto!
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Revise as informações antes de gerar seu deck
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-purple-50 dark:from-gray-800 dark:to-purple-900/20 rounded-xl p-5 space-y-4 border-2 border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Arquivos</h5>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                          <DocumentArrowUpIcon className="h-5 w-5 mr-2 text-purple-600" />
                          {selectedFiles.length ? selectedFiles.map((f) => f.name).join(', ') : 'Nenhum arquivo selecionado'}
                        </p>
                        {selectedFiles.length > 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {(selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFileSubStep(1)}
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-700 p-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Cards</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{fileUploadForm.cardCount} cards</p>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Dificuldade</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {fileUploadForm.difficulty === 'beginner' ? '🌱 Iniciante' : fileUploadForm.difficulty === 'intermediate' ? '🌿 Intermediário' : '🌳 Avançado'}
                        </p>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Idioma</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {fileUploadForm.language === 'portuguese' ? '🇧🇷 Português' : fileUploadForm.language === 'spanish' ? '🇪🇸 Español' : '🇺🇸 English'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFileSubStep(2)}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 font-medium text-left flex items-center"
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Editar configuração
                      </button>
                    </div>

                    {fileUploadForm.additionalInfo && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Info Adicional</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{fileUploadForm.additionalInfo}</p>
                      </div>
                    )}
                  </div>

                  {/* Information message */}
                  {!isSubmitting && (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-5 w-5 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1 text-sm">
                          <p className="text-purple-700 dark:text-purple-200 font-semibold mb-1">
                            💡 Processamento inteligente
                          </p>
                          <p className="text-purple-600 dark:text-purple-300">
                            Nossa IA extrairá o conteúdo mais relevante do arquivo e criará cards educativos automaticamente.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Progress indicator */}
              {isSubmitting && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-5 border-2 border-purple-200 dark:border-purple-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <DocumentArrowUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                      <span className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                        Processando arquivo e gerando cards...
                      </span>
                    </div>
                    <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                      ⏱️ ~30-90s
                    </span>
                  </div>
                  
                  <div className="w-full bg-purple-100 dark:bg-purple-800 rounded-full h-2.5 overflow-hidden">
                    <div className="h-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 dark:from-purple-400 dark:to-indigo-400 rounded-full animate-pulse"></div>
                  </div>
                  
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-3 text-center">
                    Extraindo texto e criando cards personalizados com IA...
                  </p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Back Button */}
                <button
                  type="button"
                  onClick={() => {
                    if (fileSubStep > 1) {
                      setFileSubStep(fileSubStep - 1);
                    } else {
                      setStep('choice');
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex-1 px-5 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← {fileSubStep === 1 ? 'Voltar' : 'Anterior'}
                </button>

                {/* Next/Submit Button */}
                {fileSubStep < 3 ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Validation for each step
                      if (fileSubStep === 1 && selectedFiles.length === 0) {
                        toast.error('Por favor, selecione um arquivo');
                        return;
                      }
                      setFileSubStep(fileSubStep + 1);
                    }}
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-purple-500/30"
                  >
                    Continuar →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || selectedFiles.length === 0}
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Gerando...</span>
                      </div>
                    ) : (
                      <span className="flex items-center justify-center">
                        <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                        Gerar Deck do Arquivo
                      </span>
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeckCreationModal;